import { useEffect, useState, type ReactNode } from 'react';
import { Layers } from 'lucide-react';
import { Button, Dialog, Field, Input } from '@/components/ui';
import { useCaptureTemplate } from '@/hooks/useGeo';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { idempotencyKey } from '@/utils/idempotency';
import type { SiteDetail } from '@/types/api';

/**
 * A site made a template (ADR-0047), or its template's next version when it
 * is the template's reference site. Only a new template asks for anything:
 * its name.
 */
export function CaptureTemplateDialog({ site, open, onClose }: { readonly site: SiteDetail; readonly open: boolean; readonly onClose: () => void }): ReactNode {
  const next = site.referenceOf;
  const [name, setName] = useState(site.displayName);
  useEffect(() => {
    if (open) setName(site.displayName);
  }, [open, site.displayName]);

  const capture = useCaptureTemplate(site.id, {
    onSuccess: () => {
      toast.success(next ? 'Новая версия шаблона снимается' : 'Шаблон создаётся', 'Готово через пару минут — на странице «Шаблоны»');
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      title={next ? `Новая версия «${next.name}»` : 'Сделать шаблоном'}
      description={next ? 'Тема, плагины и структура этого сайта станут следующей версией' : 'Сайт станет эталоном: его тема, плагины и структура — основой новых сайтов'}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button onClick={onClose} disabled={capture.isPending}>
            Отмена
          </Button>
          <Button
            variant="primary"
            icon={Layers}
            busy={capture.isPending}
            disabled={!next && !name.trim()}
            onClick={() => capture.mutate({ input: next ? { templateId: next.templateId } : { name: name.trim() }, key: idempotencyKey('template.capture') })}
          >
            {next ? 'Опубликовать' : 'Создать шаблон'}
          </Button>
        </>
      }
    >
      <div className="stack">
        {next ? (
          <p className="muted">Сайты шаблона обновятся, когда вы выкатите версию на странице «Шаблоны».</p>
        ) : (
          <Field label="Название шаблона" htmlFor="template-name">
            <Input id="template-name" value={name} onChange={(event) => setName(event.target.value)} autoComplete="off" maxLength={100} />
          </Field>
        )}
        {capture.isError ? <p className="field-error">{errorText(capture.error, 'Не удалось снять шаблон')}</p> : null}
      </div>
    </Dialog>
  );
}
